import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ticketService } from '../../services/api';
import { toast } from 'sonner';
import { 
  Loader2, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  Package,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Layers,
  Zap
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Format event name: 002 → M2, M002 → M2
const formatEventName = (eventName) => {
  if (!eventName) return '-';
  return eventName.replace(/\b[M]?0*(\d{1,3})\b/gi, (match, num) => `M${parseInt(num, 10)}`);
};

export const SpecialAttributionModal = ({ 
  open, 
  onOpenChange, 
  venteTicket, 
  project = 'WORLD CUP 2026',
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [validateOnSubmit, setValidateOnSubmit] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  // Fetch available ACHATs when modal opens
  useEffect(() => {
    if (open && venteTicket?.id) {
      fetchSpecialAchats();
    } else {
      // Reset state when modal closes
      setData(null);
      setSelectedIds(new Set());
      setExpandedCategories(new Set());
      setExpandedGroups(new Set());
      setValidateOnSubmit(false);
      setShowConfirmation(false);
      setConfirmationData(null);
    }
  }, [open, venteTicket?.id]);

  const fetchSpecialAchats = async () => {
    setLoading(true);
    try {
      const result = await ticketService.getSpecialAttributionAchats(venteTicket.id, project);
      setData(result);
      
      // Auto-expand original category if it exists
      if (result.categories?.length > 0) {
        const originalCat = result.categories.find(c => c.is_original);
        if (originalCat) {
          setExpandedCategories(new Set([originalCat.category]));
        } else {
          setExpandedCategories(new Set([result.categories[0].category]));
        }
      }
    } catch (error) {
      console.error('Error fetching special achats:', error);
      toast.error('Erreur lors du chargement des ACHAT disponibles');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const toggleTicket = (ticketId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
      }
      return next;
    });
  };

  const selectGroupTickets = (tickets, count) => {
    const ticketIds = tickets.slice(0, count).map(t => t.id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      tickets.forEach(t => next.delete(t.id));
      ticketIds.forEach(id => next.add(id));
      return next;
    });
  };

  // Get selected tickets info for confirmation
  const getSelectedTicketsInfo = () => {
    if (!data) return { categories: [], accounts: [] };
    
    const selectedTickets = [];
    const categoriesSet = new Set();
    const accountsSet = new Set();
    
    data.categories?.forEach(cat => {
      cat.groups?.forEach(group => {
        group.tickets?.forEach(ticket => {
          if (selectedIds.has(ticket.id)) {
            selectedTickets.push(ticket);
            categoriesSet.add(ticket.categorie || 'N/A');
            accountsSet.add(group.email_compte);
          }
        });
      });
    });
    
    return {
      tickets: selectedTickets,
      categories: Array.from(categoriesSet),
      accounts: Array.from(accountsSet)
    };
  };

  const handlePrepareSubmit = () => {
    if (selectedIds.size === 0) {
      toast.error('Sélectionnez au moins un ticket ACHAT');
      return;
    }

    const venteQty = data?.vente_qty || venteTicket?.qty || 1;
    if (selectedIds.size !== venteQty) {
      toast.error(`Sélectionnez exactement ${venteQty} billet(s). Actuellement: ${selectedIds.size}`);
      return;
    }

    // Prepare confirmation data
    const info = getSelectedTicketsInfo();
    const isDifferentCategory = !info.categories.includes(data?.vente_categorie);
    
    setConfirmationData({
      venteCategorie: data?.vente_categorie,
      achatCategories: info.categories,
      accounts: info.accounts,
      count: selectedIds.size,
      isDifferentCategory
    });
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await ticketService.specialAttribution(
        venteTicket.id,
        Array.from(selectedIds),
        validateOnSubmit,
        project
      );
      
      toast.success(result.message);
      setShowConfirmation(false);
      
      // Rafraîchir les données AVANT de fermer le modal
      if (onSuccess) {
        await onSuccess();
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error performing special attribution:', error);
      toast.error('Erreur lors de l\'attribution spéciale');
    } finally {
      setSubmitting(false);
    }
  };

  const venteQty = data?.vente_qty || venteTicket?.qty || 1;
  const isExactMatch = selectedIds.size === venteQty;
  const isTooMany = selectedIds.size > venteQty;
  const isTooFew = selectedIds.size > 0 && selectedIds.size < venteQty;
  const isInvalid = isTooMany || isTooFew;

  // Confirmation Dialog
  if (showConfirmation && confirmationData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg bg-zinc-900 border-amber-500/50">
          <DialogHeader>
            <DialogTitle className="text-xl text-amber-400 flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Confirmation - Attribution Spéciale
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <p className="text-amber-300 font-medium mb-3">
                ⚠️ Êtes-vous sûr de vouloir effectuer cette attribution spéciale ?
              </p>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">VENTE catégorie:</span>
                  <span className="text-white font-medium">{confirmationData.venteCategorie || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">ACHAT catégorie(s):</span>
                  <span className={cn(
                    "font-medium",
                    confirmationData.isDifferentCategory ? "text-amber-400" : "text-emerald-400"
                  )}>
                    {confirmationData.achatCategories.join(', ') || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Billets sélectionnés:</span>
                  <span className="text-white font-medium">{confirmationData.count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Compte(s):</span>
                  <span className="text-blue-400 font-medium truncate max-w-[200px]">
                    {confirmationData.accounts.join(', ')}
                  </span>
                </div>
              </div>
            </div>

            {confirmationData.isDifferentCategory && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm">
                <strong>ATTENTION:</strong> Vous attribuez des billets de catégorie différente !
                <br />
                VENTE: <strong>{confirmationData.venteCategorie}</strong> → ACHAT: <strong>{confirmationData.achatCategories.join(', ')}</strong>
              </div>
            )}

            {/* Validate Option */}
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={validateOnSubmit}
                onCheckedChange={setValidateOnSubmit}
                className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
              />
              <span className="text-sm text-zinc-300">
                Marquer comme <span className="text-emerald-400 font-semibold">VALIDÉ</span>
              </span>
            </label>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-700">
              <Button
                variant="outline"
                onClick={() => setShowConfirmation(false)}
                disabled={submitting}
                className="border-zinc-600 text-zinc-300"
              >
                <X className="h-4 w-4 mr-2" />
                NON - Annuler
              </Button>
              <Button
                onClick={handleConfirmSubmit}
                disabled={submitting}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Attribution...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    OUI - Confirmer
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-zinc-900 border-amber-500/30">
        <DialogHeader>
          <DialogTitle className="text-xl text-amber-400 flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Attribution Spéciale - Toutes Catégories
          </DialogTitle>
        </DialogHeader>

        {/* VENTE Info */}
        {venteTicket && (
          <div className="bg-zinc-800 rounded-lg p-4 border border-amber-500/30">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded font-bold">VENTE</span>
                  <span className="text-white font-mono text-sm">{venteTicket.ref_vente}</span>
                </div>
                <p className="text-zinc-300">{formatEventName(venteTicket.evenement)}</p>
                <p className="text-sm text-zinc-400 mt-1">
                  Catégorie demandée: <span className="text-amber-400 font-semibold">{venteTicket.categorie || 'N/A'}</span>
                  {' '} · {' '}
                  Client: <span className="text-zinc-300">{venteTicket.nom_client || 'N/A'}</span>
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-amber-400">{venteQty}</div>
                <div className="text-xs text-zinc-400">billet(s) à attribuer</div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
            <span className="ml-3 text-zinc-400">Recherche de tous les ACHAT disponibles...</span>
          </div>
        )}

        {/* No Results */}
        {!loading && data && data.total_available === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
            <p className="text-zinc-300 text-lg">Aucun ACHAT disponible</p>
            <p className="text-zinc-500 text-sm mt-2">
              Aucun ticket ACHAT disponible pour cet événement
            </p>
          </div>
        )}

        {/* Results */}
        {!loading && data && data.total_available > 0 && (
          <>
            {/* Summary */}
            <div className="flex items-center justify-between text-sm py-2 border-b border-zinc-700">
              <span className="text-zinc-400">
                <span className="text-amber-400 font-bold">{data.total_available}</span> ACHAT disponible(s) 
                dans <span className="text-purple-400">{data.categories?.length}</span> catégorie(s)
              </span>
              <div className={cn(
                "flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold",
                isExactMatch ? "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/50" :
                isInvalid ? "bg-red-500/20 text-red-400 ring-1 ring-red-500/50" :
                "bg-zinc-800 text-zinc-400"
              )}>
                {isExactMatch ? <CheckCircle className="h-4 w-4" /> :
                 isInvalid ? <AlertTriangle className="h-4 w-4" /> :
                 <Package className="h-4 w-4" />}
                <span>{selectedIds.size} / {venteQty} sélectionné(s)</span>
              </div>
            </div>

            {/* Categories List */}
            <div className="flex-1 overflow-y-auto min-h-0 space-y-3 py-2">
              {data.categories?.map((cat) => {
                const isCatExpanded = expandedCategories.has(cat.category);
                const isOriginalCat = cat.is_original;
                const selectedInCat = cat.groups?.reduce((sum, g) => 
                  sum + g.tickets.filter(t => selectedIds.has(t.id)).length, 0) || 0;
                
                return (
                  <div 
                    key={cat.category} 
                    className={cn(
                      "border rounded-lg overflow-hidden",
                      isOriginalCat 
                        ? "border-emerald-500/50 ring-1 ring-emerald-500/20" 
                        : "border-amber-500/30"
                    )}
                  >
                    {/* Category Header */}
                    <button
                      onClick={() => toggleCategory(cat.category)}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-3 transition-colors",
                        isOriginalCat 
                          ? "bg-emerald-500/10 hover:bg-emerald-500/20" 
                          : "bg-amber-500/5 hover:bg-amber-500/10"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {isCatExpanded ? 
                          <ChevronDown className="h-5 w-5 text-zinc-400" /> : 
                          <ChevronRight className="h-5 w-5 text-zinc-400" />
                        }
                        <Layers className={cn(
                          "h-5 w-5", 
                          isOriginalCat ? "text-emerald-400" : "text-amber-400"
                        )} />
                        <span className={cn(
                          "font-bold text-lg",
                          isOriginalCat ? "text-emerald-400" : "text-amber-400"
                        )}>
                          {cat.category}
                        </span>
                        {isOriginalCat && (
                          <span className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                            CATÉGORIE DEMANDÉE
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {selectedInCat > 0 && (
                          <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded">
                            {selectedInCat} sélectionné(s)
                          </span>
                        )}
                        <span className="text-zinc-400 text-sm font-medium">
                          {cat.total} billet(s) · {cat.groups?.length} compte(s)
                        </span>
                      </div>
                    </button>

                    {/* Category Content - Groups */}
                    {isCatExpanded && (
                      <div className="bg-zinc-900/50 p-3 space-y-2">
                        {cat.groups?.map((group) => {
                          const groupKey = `${cat.category}-${group.email_compte}`;
                          const isGroupExpanded = expandedGroups.has(groupKey);
                          const selectedInGroup = group.tickets.filter(t => selectedIds.has(t.id)).length;
                          const isExactMatchGroup = group.count === venteQty;
                          const hasEnough = group.count >= venteQty;
                          
                          // In Special Attribution, only show OPTIMAL if it's the ORIGINAL category AND exact match
                          // For other categories, show "AUTRE CAT" badge in red/orange
                          const isOptimalInOriginalCat = isOriginalCat && isExactMatchGroup;
                          const isDifferentCatExactMatch = !isOriginalCat && isExactMatchGroup;
                          
                          return (
                            <div 
                              key={groupKey}
                              className={cn(
                                "border rounded-lg overflow-hidden",
                                isOptimalInOriginalCat 
                                  ? "border-emerald-500/40" 
                                  : isDifferentCatExactMatch
                                    ? "border-amber-500/40"  // Different category - amber border
                                    : hasEnough
                                      ? "border-zinc-600"
                                      : "border-red-500/30"
                              )}
                            >
                              {/* Group Header */}
                              <button
                                onClick={() => toggleGroup(groupKey)}
                                className={cn(
                                  "w-full flex items-center justify-between px-3 py-2 transition-colors",
                                  isOptimalInOriginalCat 
                                    ? "bg-emerald-500/10 hover:bg-emerald-500/20" 
                                    : isDifferentCatExactMatch
                                      ? "bg-amber-500/5 hover:bg-amber-500/10"  // Different category - amber bg
                                      : "bg-zinc-800 hover:bg-zinc-700"
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  {isGroupExpanded ? 
                                    <ChevronDown className="h-4 w-4 text-zinc-400" /> : 
                                    <ChevronRight className="h-4 w-4 text-zinc-400" />
                                  }
                                  <Users className={cn(
                                    "h-4 w-4",
                                    isOptimalInOriginalCat ? "text-emerald-400" : 
                                    isDifferentCatExactMatch ? "text-amber-400" :
                                    hasEnough ? "text-blue-400" : "text-red-400"
                                  )} />
                                  <span className="text-white text-sm truncate max-w-[250px]">
                                    {group.email_compte}
                                  </span>
                                  {isOptimalInOriginalCat ? (
                                    <span className="bg-emerald-500 text-white text-[9px] font-bold px-1 py-0.5 rounded">
                                      OPTIMAL
                                    </span>
                                  ) : isDifferentCatExactMatch ? (
                                    <span className="bg-amber-500/80 text-white text-[9px] font-bold px-1 py-0.5 rounded">
                                      AUTRE CAT
                                    </span>
                                  ) : null}
                                </div>
                                <div className="flex items-center gap-2">
                                  {selectedInGroup > 0 && (
                                    <span className="bg-emerald-500/20 text-emerald-400 text-xs px-1.5 py-0.5 rounded">
                                      {selectedInGroup}
                                    </span>
                                  )}
                                  <span className="text-zinc-400 text-xs">
                                    {group.count} billet(s)
                                  </span>
                                </div>
                              </button>

                              {/* Group Content - Tickets */}
                              {isGroupExpanded && (
                                <div className="bg-zinc-900/50 p-2 space-y-1">
                                  {/* Quick Actions */}
                                  <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
                                    {group.count >= venteQty && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => { e.stopPropagation(); selectGroupTickets(group.tickets, venteQty); }}
                                        className="h-6 text-xs bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20"
                                      >
                                        Sélectionner {venteQty}
                                      </Button>
                                    )}
                                  </div>

                                  {/* Tickets */}
                                  {group.tickets.map((ticket) => {
                                    const isSelected = selectedIds.has(ticket.id);
                                    return (
                                      <div
                                        key={ticket.id}
                                        onClick={() => toggleTicket(ticket.id)}
                                        className={cn(
                                          "flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors text-xs",
                                          isSelected 
                                            ? "bg-emerald-500/20 border border-emerald-500/30" 
                                            : "bg-zinc-800/50 hover:bg-zinc-800 border border-transparent"
                                        )}
                                      >
                                        <Checkbox
                                          checked={isSelected}
                                          onCheckedChange={() => toggleTicket(ticket.id)}
                                          className="h-3 w-3 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                                        />
                                        <span className="text-zinc-300 truncate">
                                          {formatEventName(ticket.evenement)}
                                        </span>
                                        {ticket.block && (
                                          <span className="text-zinc-500">
                                            B:{ticket.block} R:{ticket.rang || '-'} P:{ticket.place || '-'}
                                          </span>
                                        )}
                                        {isSelected && <Check className="h-3 w-3 text-emerald-400 ml-auto" />}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t border-zinc-700 pt-4 space-y-3">
              {/* Warning messages */}
              {isInvalid && (
                <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/20 p-3 rounded border border-red-500/30">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <span>
                    {isTooMany 
                      ? `Trop de billets sélectionnés (${selectedIds.size}/${venteQty}). Désélectionnez ${selectedIds.size - venteQty} billet(s).`
                      : `Il manque ${venteQty - selectedIds.size} billet(s). Sélectionnez exactement ${venteQty} billet(s).`
                    }
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={submitting}
                  className="border-zinc-600 text-zinc-300"
                >
                  <X className="h-4 w-4 mr-2" />
                  Annuler
                </Button>
                <Button
                  onClick={handlePrepareSubmit}
                  disabled={submitting || selectedIds.size === 0 || isInvalid}
                  className={cn(
                    "font-semibold",
                    isInvalid 
                      ? "bg-red-500/50 text-red-200 cursor-not-allowed"
                      : isExactMatch 
                        ? "bg-amber-500 hover:bg-amber-600 text-black"
                        : "bg-zinc-600 text-zinc-400 cursor-not-allowed"
                  )}
                >
                  {isInvalid ? (
                    <>
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      {isTooMany ? `Trop (${selectedIds.size}/${venteQty})` : `Manque ${venteQty - selectedIds.size}`}
                    </>
                  ) : isExactMatch ? (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Attribution Spéciale →
                    </>
                  ) : (
                    <>
                      <Package className="h-4 w-4 mr-2" />
                      Sélectionnez {venteQty} billet(s)
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
